'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { PhoneCall } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Trail } from './demoParts'

// Voice AI: a live inbound call. The transcript fills in while the waveform
// shows who is talking, and GoHighLevel updates on the right as the agent
// learns things. Two calls: a new lead it books, and one it hands to a person.

const CALLS = {
  lead: {
    caller: 'New caller · (512) 555-0148',
    lines: [
      { who: 'agent', text: 'Hi, this is Sarah from Bright Home Services. How can I help?' },
      { who: 'caller', text: "I'd like a quote to replace my AC unit." },
      { who: 'agent', text: 'Happy to help. Roughly what budget do you have in mind, and how soon do you need it?' },
      { who: 'caller', text: 'Around five thousand, ideally this month.' },
      { who: 'agent', text: 'I can send a technician Tuesday at 9 AM or Wednesday at 1 PM. Which works?' },
      { who: 'caller', text: 'Tuesday at 9.' },
      { who: 'agent', text: "You're booked. I've just texted you the confirmation." },
    ],
    trail: [
      { at: 2, text: 'Contact created' },
      { at: 4, text: 'Budget ~$5,000 · timeline this month' },
      { at: 7, text: 'Appointment booked: Tue 9:00 AM' },
      { at: 8, text: 'Confirmation text sent' },
      { at: 8, text: 'Opportunity added to the pipeline' },
    ],
    result: { tone: 'good', text: 'Booked · no voicemail' },
  },
  human: {
    caller: 'Existing client · Jordan Ellis',
    lines: [
      { who: 'agent', text: 'Hi, this is Sarah from Bright Home Services. How can I help?' },
      { who: 'caller', text: "I've got a question about a charge on last month's invoice." },
      { who: 'agent', text: "Sorry about that, Jordan. Billing questions go to our accounts team, so I'm putting you through now with the details." },
      { who: 'caller', text: 'Great, thanks.' },
    ],
    trail: [
      { at: 2, text: 'Matched to Jordan Ellis in your CRM' },
      { at: 3, text: 'Recognised a billing question' },
      { at: 4, text: 'Transferred to the accounts team' },
      { at: 5, text: 'Call summary saved to the contact' },
    ],
    result: { tone: 'good', text: 'Handed to your team' },
  },
}

const delaysFor = (lines) => [...lines.map((l, i) => (i === 0 ? 600 : 1500 + Math.min(1200, l.text.length * 12))), 900]
const DELAYS = { lead: delaysFor(CALLS.lead.lines), human: delaysFor(CALLS.human.lines) }

/** Bars that move while someone is speaking; colour follows the speaker. */
function Waveform({ speaker }) {
  return (
    <div className={`svd-wave is-${speaker || 'idle'}`} aria-hidden="true">
      {Array.from({ length: 28 }, (_, i) => <i key={i} style={{ animationDelay: `${(i % 7) * 0.09}s` }} />)}
    </div>
  )
}

export default function VoiceCall() {
  const [ref, onScreen] = useOnScreen()
  const [call, setCall] = useState('lead')
  const [run, setRun] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const data = CALLS[call]
  const step = useSequence(DELAYS[call], { run: onScreen, resetKey: `${call}-${run}` })
  const shown = data.lines.slice(0, Math.min(step, data.lines.length))
  const finished = step >= DELAYS[call].length
  const speaking = !finished && step < data.lines.length ? data.lines[step]?.who : null

  const [timerKey, setTimerKey] = useState(`${call}-${run}`)
  if (timerKey !== `${call}-${run}`) { setTimerKey(`${call}-${run}`); setSeconds(0) }
  useEffect(() => {
    if (!onScreen || finished) return undefined
    const t = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [onScreen, finished, timerKey])

  const pick = (key) => { setCall(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-voice"
      title="A live call, answered by AI"
      label="Demo: a voice AI agent answering a phone call and updating GoHighLevel"
      tabs={[{ key: 'lead', label: 'New lead calls' }, { key: 'human', label: 'Needs a person' }]}
      activeTab={call}
      onTab={pick}
      onReplay={() => pick(call)}
    >
      <div className="svd-agent-grid">
        <div className="svd-call">
          <div className="svd-call-head">
            <span className="svd-call-icon" aria-hidden="true"><PhoneCall size={16} /></span>
            <span>
              <strong>{data.caller}</strong>
              <small>{finished ? 'Call ended' : 'On call with your AI agent'} · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</small>
            </span>
          </div>
          <Waveform speaker={speaking} />
          <div className="svd-transcript" aria-live="polite">
            <AnimatePresence initial={false}>
              {shown.map((line, i) => (
                <motion.p key={`${call}-${run}-${i}`} className={`is-${line.who}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <b>{line.who === 'agent' ? 'AI agent' : 'Caller'}</b> {line.text}
                </motion.p>
              ))}
            </AnimatePresence>
          </div>
        </div>
        <Trail title="In GoHighLevel" items={data.trail} step={step} />
      </div>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${call}-${run}`} />
    </DemoFrame>
  )
}
